<template>
  <v-dialog
    v-model="show"
    v-bind="$attrs"
    :fullscreen="fullscreen"
    persistent
    scrollable
    :content-class="fullscreen ? 'fixed-safe-area--full' : ''"
    transition="scale-transition"
  >
    <template #activator="slotAttrs">
      <slot name="activator" v-bind="slotAttrs" />
    </template>
    <v-card v-if="show">
      <EmLoadingBar :loading="loading" data-cy-form-field="dialog-loader" />
      <div class="flex-grow-0 d-flex justify-space-between align-center pa-4">
        <div class="d-flex align-center">
          <EmIcon v-if="showGoBack" icon="mdi-chevron-left" @click="goBack" />
          <EmBody1 semi-bold>
            {{ title }}
          </EmBody1>
          <slot name="title.append" />
        </div>
        <EmIcon v-if="!showGoBack && canClose" icon="mdi-close" aria-label="dialog-cancel" @click="close" />
      </div>
      <EmDivider />
      <div class="em-scrollbar flex-grow-1" :class="{'pa-4': !noPadding}">
        <slot />
      </div>
      <EmDivider />
      <div class="d-flex align-center px-4">
        <slot name="extra-info" />
        <div v-if="!hideActions" class="flex-grow-1 d-flex justify-end py-4 ga-2">
          <slot name="buttons">
            <EmTerciaryButton v-if="showCancel" aria-label="dialog-cancel" :disabled="!canClose" @click="close">
              {{ cancelText }}
            </EmTerciaryButton>
            <slot name="extra-buttons" />
            <EmButton
              v-if="showConfirm"
              v-bind="confirmButtonProps"
              aria-label="dialog-confirm"
              :data-cy="show ? 'dialog-confirm' : ''"
              :disabled="!canConfirm"
              :loading="loading"
              @click="ev=> $emit('confirm', ev)"
            >
              {{ confirmText }}
            </EmButton>
          </slot>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useDisplay } from 'vuetify'
// Vuetify's parent-menu chain (VMenuSymbol) crosses dialog boundaries: menus
// rendered inside this dialog would inject a v-menu that contains the dialog
// (e.g. when activated from a menu list) and close it on outside clicks,
// unmounting the dialog with it. Sever the chain at the dialog boundary.
provide(Symbol.for('vuetify:v-menu'), null)
const display = ref(useDisplay())
const emits = defineEmits(['open', 'close', 'go-back', 'cancel', 'confirm'])
const show = defineModel({
  type: Boolean,
  default: false,
  set (value) {
    value ? emits('open') : emits('close')
    return value
  }
})
defineProps({
  title: { type: String, default: '' },
  cancelText: { type: String, default: 'Close' },
  confirmText: { type: String, default: 'Confirm' },
  canConfirm: { type: Boolean, default: true },
  canClose: { type: Boolean, default: true },
  showCancel: { type: Boolean, default: true },
  showConfirm: { type: Boolean, default: true },
  showGoBack: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  hideActions: { type: Boolean, default: false },
  noPadding: { type: Boolean, default: false },
  confirmButtonProps: { type: Object, default: () => ({}) }
})
const fullscreen = computed(() => display.value.smAndDown)
const open = () => { show.value = true }
const close = () => {
  show.value = false
  emits('cancel')
}
const goBack = () => emits('go-back')
defineExpose({ open, close })
</script>
